import React, { useEffect, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as XLSX from 'xlsx';
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { 
  FiEdit, FiTrash, FiPlus, FiX, FiPower, FiAlertTriangle, FiSearch, FiChevronLeft, FiChevronRight 
} from 'react-icons/fi';
import ApiServices from '../../services/apiservices';

const AdminMeterPriceManager = () => {
  const [posts, setPosts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  
  // Filter States
  const [searchTitle, setSearchTitle] = useState("");
  const [searchValidity, setSearchValidity] = useState("");
  const [searchStatus, setSearchStatus] = useState("All");

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage, setPostsPerPage] = useState(5);

  // States for the Delete Confirmation
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);

  const normalizeRows = (rows) => {
    if (!Array.isArray(rows)) return [];
    return rows.map(row => {
      const cleanRow = {};
      Object.keys(row).forEach(key => {
        const cleanKey = key.trim().replace(/\u00A0/g, ' ');
        cleanRow[cleanKey] = typeof row[key] === 'string' ? row[key].trim() : row[key];
      });
      return cleanRow;
    });
  };

  const fetchPosts = async () => {
    try {
      const res = await ApiServices.getRequest("/meter-prices/fetch.php");
      const data = res.posts || res;
      const normalizedData = Array.isArray(data) ? data.map(post => ({
        ...post,
        rows: normalizeRows(post.rows)
      })) : [];
      setPosts(normalizedData);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch meter prices");
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  // ================= FILTERS =================
  const filteredPosts = posts.filter((post) => {
    const matchesTitle = post.title?.toLowerCase().includes(searchTitle.toLowerCase());
    const matchesValidity = post.validity?.toLowerCase().includes(searchValidity.toLowerCase());
    const matchesStatus = searchStatus === "All" || post.status === searchStatus;

    return matchesTitle && matchesValidity && matchesStatus;
  });

  // ================= PAGINATION =================
  const indexOfLast = currentPage * postsPerPage;
  const currentPosts = filteredPosts.slice(
    indexOfLast - postsPerPage,
    indexOfLast
  );
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTitle, searchValidity, searchStatus]);

  const handleToggleStatus = async (post) => {
    const newStatus = post.status === "PUBLISHED" ? "DRAFT" : "PUBLISHED";
    try {
      await ApiServices.putRequest(`/meter-prices/update.php?id=${post.id}`, { ...post, status: newStatus });
      toast.success(`Post marked as ${newStatus}`);
      fetchPosts();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async () => {
    if (!postToDelete) return;
    try {
      await ApiServices.deleteRequest(`/meter-prices/delete.php?id=${postToDelete.id}`);
      toast.success("Post deleted successfully");
      setIsDeleteModalOpen(false);
      setPostToDelete(null);
      fetchPosts();
    } catch (err) {
      toast.error("Failed to delete post");
    }
  };

  const handleFileUpload = (e, setFieldValue) => {
    const file = e.currentTarget.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: 'binary' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rawData = XLSX.utils.sheet_to_json(ws);
      const cleanData = normalizeRows(rawData);
      setFieldValue("rows", cleanData);
      toast.info(`${cleanData.length} rows loaded from Excel`);
    };
    reader.readAsBinaryString(file);
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      if (selectedPost) {
         const res = await ApiServices.putRequest(`/meter-prices/update.php?id=${selectedPost.id}`, values);
         if (res.success){
          toast.success(res.message);
        }else{
          toast.error(res.message || "Submission failed");
        }
      } else {
        const res = await ApiServices.postRequest('/meter-prices/create.php', values);
        if (res.success){
          toast.success(res.message);
        }else{
          toast.error(res.message || "Submission failed");
        }
      }
     
      setIsModalOpen(false);
      resetForm();
      fetchPosts();
    } catch (error) {
      toast.error(error?.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Meter Price Management</h2>
          <p className="text-gray-500 text-sm">Total matching records: {filteredPosts.length}</p>
        </div>
        <button
          onClick={() => { setSelectedPost(null); setIsModalOpen(true); }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 shadow-md transition text-sm font-medium"
        >
          <FiPlus /> Create New Post
        </button>
      </div>

      {/* FILTERS BAR */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Search Title */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Search Title</label>
          <div className="relative">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
              <FiSearch size={14} />
            </span>
            <input
              type="text"
              placeholder="Filter by title..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTitle}
              onChange={(e) => setSearchTitle(e.target.value)}
            />
          </div>
        </div>

        {/* Validity Filter */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Validity Period</label>
          <input
            type="text"
            placeholder="Filter by validity..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
            value={searchValidity}
            onChange={(e) => setSearchValidity(e.target.value)}
          />
        </div>

        {/* Status Filter */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Status</label>
          <select
            value={searchStatus}
            onChange={(e) => setSearchStatus(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="All">All Statuses</option>
            <option value="PUBLISHED">Published</option>
            <option value="DRAFT">Draft</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-gray-100 border-b">
            <tr>
              <th className="p-4 font-semibold text-gray-600">Title</th>
              <th className="p-4 font-semibold text-gray-600">Validity</th>
              <th className="p-4 font-semibold text-gray-600">Rows</th>
              <th className="p-4 font-semibold text-gray-600">Status</th>
              <th className="p-4 font-semibold text-gray-600 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {currentPosts.length > 0 ? (
              currentPosts.map((post) => (
                <tr key={post.id} className="hover:bg-gray-50">
                  <td className="p-4 font-medium text-blue-900">{post.title}</td>
                  <td className="p-4 text-gray-600">{post.validity}</td>
                  <td className="p-4 text-gray-500">{post.rows?.length || 0} items</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-[10px] uppercase font-bold ${
                      post.status === "PUBLISHED" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-600"
                    }`}>
                      {post.status}
                    </span>
                  </td>
                  <td className="p-4 flex gap-4 justify-center items-center">
                    <button 
                      onClick={() => handleToggleStatus(post)} 
                      title={post.status === "PUBLISHED" ? "Deactivate" : "Activate"}
                      className={`${post.status === "PUBLISHED" ? "text-orange-500" : "text-green-500"} hover:scale-110 transition`}
                    >
                      <FiPower size={18} />
                    </button>
                    <button onClick={() => { setSelectedPost(post); setIsModalOpen(true); }} className="text-blue-600 hover:scale-110 transition"><FiEdit size={18} /></button>
                    <button 
                      onClick={() => { setPostToDelete(post); setIsDeleteModalOpen(true); }} 
                      className="text-red-500 hover:scale-110 transition"
                    >
                      <FiTrash size={18} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="p-8 text-center text-gray-400">
                  No meter prices found matching your criteria.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINATION & LIMIT CONTROLS */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 px-2">
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500 font-medium">
            Showing page <span className="font-bold text-gray-700">{currentPage}</span> of <span className="font-bold text-gray-700">{totalPages || 1}</span> ({filteredPosts.length} matching entries)
          </span>
          <select
            value={postsPerPage}
            onChange={(e) => {
              setPostsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="px-2 py-1 border border-gray-300 rounded-lg text-xs bg-white outline-none"
          >
            <option value="5">5 per page</option>
            <option value="10">10 per page</option>
            <option value="20">20 per page</option>
            <option value="50">50 per page</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
            className="px-3 py-2 border rounded-lg bg-white disabled:opacity-40 text-xs font-semibold flex items-center gap-1 shadow-sm hover:bg-gray-50 transition"
          >
            <FiChevronLeft size={14} /> Previous
          </button>

          <span className="text-xs font-medium px-2">
            Page {currentPage} of {totalPages || 1}
          </span>

          <button
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={() => setCurrentPage((p) => p + 1)}
            className="px-3 py-2 border rounded-lg bg-white disabled:opacity-40 text-xs font-semibold flex items-center gap-1 shadow-sm hover:bg-gray-50 transition"
          >
            Next <FiChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* CREATE/EDIT MODAL */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center p-4">
            <motion.div initial={{ y: 50 }} animate={{ y: 0 }} className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b flex justify-between items-center bg-gray-50">
                <h3 className="text-xl font-bold">{selectedPost ? 'Edit' : 'Create'} Meter Price</h3>
                <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-red-500"><FiX size={24} /></button>
              </div>

              <div className="p-8 overflow-y-auto flex-1">
                <Formik
                  initialValues={{
                    title: selectedPost?.title || '',
                    description: selectedPost?.description || '',
                    validity: selectedPost?.validity || '',
                    rows: selectedPost?.rows || [],
                    status: selectedPost?.status || 'PUBLISHED'
                  }}
                  onSubmit={handleSubmit}
                >
                  {({ setFieldValue, values, isSubmitting }) => (
                    <Form className="space-y-6">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="col-span-1">
                          <label className="block text-sm font-bold mb-1">Post Title</label>
                          <Field name="title" className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none" />
                        </div>
                        <div className="col-span-1">
                          <label className="block text-sm font-bold mb-1">Validity Period</label>
                          <Field name="validity" className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none" />
                        </div>
                        <div className="col-span-1">
                          <label className="block text-sm font-bold mb-1">Status</label>
                          <Field as="select" name="status" className="w-full p-2 border rounded bg-white focus:ring-2 focus:ring-blue-500 outline-none">
                            <option value="PUBLISHED">PUBLISHED</option>
                            <option value="DRAFT">DRAFT</option>
                          </Field>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-bold mb-1">Description</label>
                        <Field as="textarea" name="description" className="w-full p-2 border rounded h-20 outline-none" />
                      </div>

                      <div className="border rounded-xl p-4 bg-gray-50">
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-bold text-blue-800">Meter Pricing Data</h4>
                          <input type="file" accept=".xlsx,.xls" onChange={(e) => handleFileUpload(e, setFieldValue)} className="text-xs" />
                        </div>

                        <div className="max-h-64 overflow-auto border rounded bg-white">
                          <table className="w-full text-[12px]">
                            <thead className="bg-gray-200 sticky top-0">
                              <tr>
                                <th className="p-2 border">S/N</th>
                                <th className="p-2 border">MAP Name</th>
                                <th className="p-2 border">1-Phase</th>
                                <th className="p-2 border">3-Phase</th>
                                <th className="p-2 border">Din Rail</th>
                              </tr>
                            </thead>
                            <tbody>
                              {values.rows.map((row, idx) => (
                                <tr key={idx}>
                                  <td className="p-1 border text-center">{idx + 1}</td>
                                  {["Name of MAP", "1-Phase", "3-Phase", "1-Phase Din Rail"].map((col) => (
                                    <td key={col} className="p-1 border">
                                      <input 
                                        className="w-full outline-none px-1" 
                                        value={row[col] || ""} 
                                        onChange={(e) => {
                                          const newRows = [...values.rows];
                                          newRows[idx][col] = e.target.value;
                                          setFieldValue("rows", newRows);
                                        }} 
                                      />
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="flex gap-4">
                        <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 p-3 border rounded-lg font-bold hover:bg-gray-50 transition">Cancel</button>
                        <button type="submit" disabled={isSubmitting} className="flex-1 p-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition">
                          {isSubmitting ? 'Processing...' : (selectedPost ? 'Update Post' : 'Create Post')}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* DELETE CONFIRMATION MODAL */}
      <AnimatePresence>
        {isDeleteModalOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex justify-center items-center p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="bg-white rounded-xl p-6 max-w-sm w-full text-center shadow-2xl">
              <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiAlertTriangle size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Are you sure?</h3>
              <p className="text-gray-500 mt-2">You are about to delete <span className="font-semibold text-gray-700">"{postToDelete?.title}"</span>. This action cannot be undone.</p>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setIsDeleteModalOpen(false)} className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg font-semibold transition">Cancel</button>
                <button onClick={handleDelete} className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition">Delete</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminMeterPriceManager;